import React from "react";
import { shallowEqual, useSelector } from "react-redux";
import Styles from "./SubscriptionItem.module.scss"
import {IHistoryItemProps} from "./types";
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import subscriptionIcon from "../../../../assets/icons/subscription.svg";
import gymIcon from "../../../../assets/icons/gym.png";
import poolIcon from "../../../../assets/icons/whaterpool.png";
import saunaIcon from "../../../../assets/icons/sauna.png";
import playroomIcon from "../../../../assets/icons/igroteka.png";
import calendarIcon from "../../../../assets/icons/calendar.svg";
import { Button, NoticeBanner } from "../../../../components";
import { ISubscription } from "../../../../redux/subscriptions/types";
import { selectNoticesFor } from "../../../../redux/notices/selectors";

interface ISubscriptionItemProps extends IHistoryItemProps {
    status: string;
    subscriptions?: ISubscription[];
}

const SubscriptionItem: React.FC<ISubscriptionItemProps> = (props) => {
    const { status, subscriptions = [] } = props;
    const isFamily = props.data.subscription?.type === 'family';
    
    const dateEndStr = props.data.date_to_iso || props.data.expires_at;
    const dateStartStr = props.data.date_from_iso || props.data.bought_at;
    
    const dateEnd = dateEndStr ? new Date(dateEndStr) : new Date();
    const dateStart = dateStartStr ? new Date(dateStartStr) : new Date();
    
    const formatDate = (dateObj: Date): string => {
        if (isNaN(dateObj.getTime())) return '';
        return format(dateObj, 'EEE, d MMMM yyyy', { locale: ru });
    };
    
    const formattedDateStart = formatDate(dateStart);
    const formattedDateEnd = formatDate(dateEnd);
    
    const isDiscountPeriod = props.data.isDiscountPeriod || false;
    const now = new Date();
    const isExpired = dateEnd && !isNaN(dateEnd.getTime()) && dateEnd < now;
    const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    const isExpiring = !isExpired && (isDiscountPeriod || (dateEnd && !isNaN(dateEnd.getTime()) && dateEnd <= sevenDaysFromNow && dateEnd >= now));
    
    const membershipId = props.data.membershipId || props.data.subscription_id || props.data.subscription?.id;
    const subItem = membershipId && subscriptions.length > 0 
        ? subscriptions.find(sub => String(sub.id) === String(membershipId))
        : null;
    
    // Объявления для купленного абонемента; без membershipId — только «для всех»
    const activeNotices = useSelector(
        selectNoticesFor("ACTIVE_SUBSCRIPTION", membershipId),
        shallowEqual
    );
    
    const renewalDiscount = subItem?.discounts?.find(el => el.type === 'renewal');
    const renewalPrice = renewalDiscount 
        ? (subItem?.price || 0) - (renewalDiscount.value || 0)
        : subItem?.price || null;
    
    const formatPrice = (price: number): string => {
        return new Intl.NumberFormat('ru-RU').format(price);
    };
    
    const originalPrice = props.data.price || props.data.subscription?.price || subItem?.price || 0;
    const discountedPrice = isExpiring && renewalPrice ? renewalPrice : null;
    
    const ownerName = props.data.profile?.name || '';
    
    let users: string[] = [];
    if (isFamily) {
        if (props.data.family && props.data.family.length > 0) {
            users = props.data.family.map(item => item.profile.name);
        } else if (props.data.additionalOwners && props.data.additionalOwners.length > 0) {
            if (ownerName) {
                users.push(ownerName);
            }
            props.data.additionalOwners.forEach((owner: any) => {
                const ownerName = owner.partnerName || owner.name;
                if (ownerName && !users.includes(ownerName)) {
                    users.push(ownerName);
                }
            });
        }
    }
    
    const includes = props.data.subscription?.includes || '';
    const includesStr = Array.isArray(includes) ? includes.join(',') : includes;
    const hasGym = includesStr.includes('gym');
    const hasPool = includesStr.includes('pool') || includesStr.includes('hotTub');
    const hasSauna = includesStr.includes('sauna') || includesStr.includes('spa');
    const hasPlayroom = includesStr.includes('playroom') || includesStr.includes('igroteka');
    
    const minPeople = isFamily ? 3 : null;
    
    if (status === "small") {
        return (
            <div className={Styles.wrapper_small}>
                <div className={Styles.wrapper_small_header}>
                    {props.data.subscription?.title}
                </div>
                <div className={Styles.includes_small_containterSmall}>
                    <div className={Styles.includes_small}>
                        <div>
                            <img src={calendarIcon} alt="Календарь" />
                        </div>
                        <div>
                            <div className={Styles.includes_item_text}>
                                Дата: C {formattedDateStart}
                            </div>
                            <div className={Styles.includes_item_text_foottext}>
                                По {formattedDateEnd}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    
    return (
        <div className={`${Styles.wrapper} ${isFamily ? Styles.wrapper_family : ''}`}>
            <div className={Styles.header}>
                <div className={Styles.header_left}>
                    <div className={Styles.icon}>
                        <img src={subscriptionIcon} alt="Абонемент" />
                    </div>
                    <div className={Styles.title}>{props.data.subscription?.title || 'Абонемент'}</div>
                </div>
                {originalPrice > 0 && (
                    <div className={Styles.price}>{formatPrice(originalPrice)} Р</div>
                )}
            </div>
            
            {!isExpired && <NoticeBanner notices={activeNotices} />}
            
            <div className={Styles.details}>
                <div className={Styles.row}>
                    <span className={Styles.label}>Начало действия</span>
                    <span className={Styles.value}>{formattedDateStart}</span>
                </div>
                <div className={Styles.row}>
                    <span className={Styles.label}>Конец действия</span>
                    <span className={Styles.value}>{formattedDateEnd}</span>
                </div>
                <div className={Styles.row}>
                    <span className={Styles.label}>Владелец</span>
                    <span className={Styles.value}>{ownerName}</span>
                </div>
                {isFamily && users.length > 0 && (
                    <div className={Styles.row}>
                        <span className={Styles.label}>Пользователи</span>
                        <div className={Styles.users_tags}>
                            {users.map((user, index) => (
                                <span key={index} className={Styles.user_tag}>
                                    {user.length > 20 ? `${user.substring(0, 20)}...` : user}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            
            <div className={Styles.includes_section}>
                <div className={Styles.includes_title}>Включено</div>
                <div className={Styles.includes_tags}>
                    {hasGym && (
                        <div className={Styles.include_tag}>
                            <img src={gymIcon} alt="Фитнес зал" />
                            <span>Фитнес зал</span>
                        </div>
                    )}
                    {hasPool && (
                        <div className={Styles.include_tag}>
                            <img src={poolIcon} alt="Бассейн" />
                            <span>Бассейн</span>
                        </div>
                    )}
                    {hasSauna && (
                        <div className={Styles.include_tag}>
                            <img src={saunaIcon} alt="Сауна" />
                            <span>Сауна</span>
                        </div>
                    )}
                    {hasPlayroom && (
                        <div className={Styles.include_tag}>
                            <img src={playroomIcon} alt="Игротека" />
                            <span>Игротека</span>
                        </div>
                    )}
                </div>
            </div>
            
            {isExpiring && (
                <div className={Styles.expiration_warning}>
                    Истекает срок действия абонемента, продлите сейчас со скидкой!
                </div>
            )}
            
            {isExpiring && discountedPrice && (
                <div className={Styles.pricing}>
                    <div className={Styles.discounted_price}>{formatPrice(discountedPrice)}Р</div>
                    {renewalDiscount && (
                        <div className={Styles.original_price_striked}>{formatPrice(originalPrice)}Р</div>
                    )}
                </div>
            )}
            
            {isExpiring && (
                <div className={Styles.renew_button_wrapper}>
                    <Button onClickFunc={() => {}}>Продлить</Button>
                </div>
            )}
            
            <div className={Styles.notes}>
                <div className={Styles.note}>*Не включены групповые занятия</div>
                {isFamily && minPeople && (
                    <div className={Styles.note}>*Минимальное количество людей - {minPeople}</div>
                )}
            </div>
        </div>
    );
}

export default SubscriptionItem;
